/**
 * Administration → Identity Providers (VSNP-1164)
 *
 * Bring-your-own IdP, per tenant. The customer registers an app in their IdP
 * (Azure AD / Entra ID, or any OIDC provider), pastes the client id + secret
 * here, and Keycloak brokers the login: their users sign in at THEIR IdP, land
 * in THIS tenant (the broker stamps the tenant on every user it admits), and
 * their IdP groups map onto this tenant's User Groups for policy.
 *
 * The client secret is write-only — it goes into Keycloak and is never shown
 * again. What the admin needs back is the broker redirect URI to register on
 * the IdP side, which is why it is displayed prominently after creation.
 *
 * Sign-in routing by email domain (VSNP-1629, doc 28d §10): each provider has
 * a card with its email domains (add → pending + the DNS TXT record → Verify,
 * or a SuperAdmin's Attest; Remove) and its routing ("Everyone in the tenant"
 * or "Only these domains"). Every write that changes how users sign in —
 * routing, a domain removal or attestation, disabling or deleting a provider
 * of a domain-mode tenant — goes through the preview dialog first. A
 * SuperAdmin also sees every claimed domain across tenants (§10.4), and the
 * page lists the recent identity-provider activity (§10.3).
 */

import { useCallback, useEffect, useState } from 'react';
import { Plus, RefreshCw, Trash2, KeyRound, Pencil, PlugZap, Copy, CheckCircle2, XCircle } from 'lucide-react';
import DataTable, { type Column } from '../../components/common/DataTable';
import StatCard from '../../components/common/StatCard';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import Modal from '../../components/common/Modal';
import StatusBadge from '../../components/common/StatusBadge';
import AccessFormField from '../../components/access/AccessFormField';
import ProviderRoutingCard, { NoVerifiedDomainBadge } from '../../components/access/ProviderRoutingCard';
import RoutingPreviewDialog from '../../components/access/RoutingPreviewDialog';
import ClaimedDomainsCard from '../../components/access/ClaimedDomainsCard';
import IdpActivityCard from '../../components/access/IdpActivityCard';
import { useToast } from '../../components/common/ToastContainer';
import accessService, {
  confirmRequiredPreview,
  type IdentityProvider,
  type IdentityProviderTestResult,
  type IdentityProviderType,
  type RoutingPreview,
} from '../../services/access.service';
import { useAuthStore } from '../../stores/authStore';
import { USER_TYPES } from '../../utils/constants';
import { apiErrorMessage } from '../../utils/apiError';
import {
  PROVIDER_TYPE_LABEL,
  domainLabel,
  needsVerifiedDomainNotice,
  providerDomains,
  providerRouting,
  tenantRoutingMode,
} from '../../utils/signInRouting';

const TYPE_LABEL: Record<IdentityProviderType, string> = PROVIDER_TYPE_LABEL;

const IdentityProviders = () => {
  const toast = useToast();
  const isSuperAdmin = useAuthStore((s) => s.user?.type === USER_TYPES.SUPER_ADMIN);
  const [providers, setProviders] = useState<IdentityProvider[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Bumped after every write, so the activity card (and the SuperAdmin's
  // claimed-domain list) re-read what the write recorded.
  const [activityKey, setActivityKey] = useState(0);

  const [editorTarget, setEditorTarget] = useState<IdentityProvider | null | 'new'>(null);
  const [deleteTarget, setDeleteTarget] = useState<IdentityProvider | null>(null);
  const [deleting, setDeleting] = useState(false);
  // Deleting a provider of a domain-mode tenant moves its users (28d §7.3):
  // the preview dialog replaces the plain confirm, and confirm=true is sent.
  const [deletePreview, setDeletePreview] = useState<{ provider: IdentityProvider; preview: RoutingPreview | null; loading: boolean; error: string | null } | null>(null);
  const [testResults, setTestResults] = useState<Record<string, IdentityProviderTestResult | 'running'>>({});

  const fetchProviders = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setProviders(await accessService.listIdentityProviders());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load identity providers');
      setProviders([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchProviders();
  }, [fetchProviders]);

  const refreshAfterWrite = useCallback(async () => {
    await fetchProviders();
    setActivityKey((k) => k + 1);
  }, [fetchProviders]);

  const mode = tenantRoutingMode(providers);

  const runTest = async (p: IdentityProvider) => {
    setTestResults((r) => ({ ...r, [p.id]: 'running' }));
    try {
      const result = await accessService.testIdentityProvider(p.id);
      setTestResults((r) => ({ ...r, [p.id]: result }));
      if (result.ok) toast.success('Provider reachable', `${p.displayName} — discovery OK in ${result.latencyMs} ms`);
      else toast.error('Provider test failed', result.error);
    } catch (err) {
      setTestResults((r) => ({ ...r, [p.id]: { ok: false, latencyMs: 0, error: err instanceof Error ? err.message : 'Test failed' } }));
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      await accessService.deleteIdentityProvider(deleteTarget.id);
      toast.success('Identity provider deleted', deleteTarget.displayName);
      setDeleteTarget(null);
      await refreshAfterWrite();
    } catch (err) {
      const preview = confirmRequiredPreview(err);
      if (preview) {
        // The API wants a confirmation (its users would move): show who.
        setDeletePreview({ provider: deleteTarget, preview, loading: false, error: null });
        setDeleteTarget(null);
      } else {
        toast.error('Delete failed', apiErrorMessage(err, 'Could not delete the provider'));
      }
    } finally {
      setDeleting(false);
    }
  };

  // A delete that moves users needs the preview first: in domain mode (its
  // users go to the password), and — once the tenant has a verified domain —
  // in all mode too, where the new apps stop finding the provider before
  // sign-in (VSNP-1707). A tenant without either deletes as before; a 409
  // CONFIRM_REQUIRED still reaches the same dialog (handleDelete).
  const startDelete = async (p: IdentityProvider) => {
    const hasVerifiedDomain = providers.some((x) => providerDomains(x).some((d) => d.status === 'verified'));
    if (mode !== 'domains' && !hasVerifiedDomain) {
      setDeleteTarget(p);
      return;
    }
    setDeletePreview({ provider: p, preview: null, loading: true, error: null });
    try {
      const preview = await accessService.getRoutingPreview({ providerId: p.id, delete: true });
      setDeletePreview({ provider: p, preview, loading: false, error: null });
    } catch (err) {
      setDeletePreview({ provider: p, preview: null, loading: false, error: apiErrorMessage(err, 'preview unavailable') });
    }
  };

  const confirmPreviewDelete = async () => {
    if (!deletePreview) return;
    const { provider, preview } = deletePreview;
    try {
      setDeleting(true);
      await accessService.deleteIdentityProvider(provider.id, { confirm: preview?.changed === true });
      toast.success('Identity provider deleted', provider.displayName);
      setDeletePreview(null);
      await refreshAfterWrite();
    } catch (err) {
      const fresh = confirmRequiredPreview(err);
      if (fresh) {
        setDeletePreview({ provider, preview: fresh, loading: false, error: null });
        toast.error('Confirm again', 'The number of users affected changed; review the preview.');
      } else {
        toast.error('Delete failed', apiErrorMessage(err, 'Could not delete the provider'));
      }
    } finally {
      setDeleting(false);
    }
  };

  const copy = async (text: string, what: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copied', what);
    } catch {
      toast.error('Copy failed', 'Select and copy the value manually');
    }
  };

  const columns: Column<IdentityProvider>[] = [
    {
      key: 'displayName',
      label: 'Provider',
      sortable: true,
      render: (p) => (
        <div>
          <div className="font-medium text-gray-900">{p.displayName}</div>
          <div className="text-xs text-gray-500">{TYPE_LABEL[p.providerType]} · <span className="font-mono">{p.alias}</span></div>
        </div>
      ),
    },
    {
      key: 'issuer',
      label: 'Issuer',
      render: (p) => <span className="text-xs font-mono text-gray-600 break-all">{p.issuer}</span>,
    },
    { key: 'clientId', label: 'Client ID', render: (p) => <span className="text-xs font-mono text-gray-600">{p.clientId}</span> },
    {
      key: 'enabled',
      label: 'Status',
      render: (p) => <StatusBadge status={p.enabled ? 'active' : 'inactive'} />,
    },
    {
      key: 'routing',
      label: 'Routing',
      // 28d §10.1: everyone, or the users of its verified domains; a pending
      // domain is listed greyed (it routes nobody yet).
      render: (p) => {
        const domains = providerDomains(p);
        if (providerRouting(p) === 'all') {
          // All mode: everyone after sign-in; the verified domains are how the
          // new apps find the provider BEFORE sign-in (VSNP-1629 follow-up).
          const found = domains.filter((d) => d.status === 'verified');
          const pending = domains.filter((d) => d.status !== 'verified');
          return (
            <div className="text-xs space-y-0.5" data-testid="idp-routing-cell">
              <span className="block text-gray-800">Everyone in this tenant</span>
              {found.length > 0 && (
                <span className="block text-gray-500">Found by {found.map((d) => `@${domainLabel(d)}`).join(', ')}</span>
              )}
              {pending.map((d) => (
                <span key={d.id} className="block text-gray-400">@{domainLabel(d)} — pending</span>
              ))}
              {needsVerifiedDomainNotice(p) && <NoVerifiedDomainBadge />}
            </div>
          );
        }
        const verified = domains.filter((d) => d.status === 'verified');
        const pending = domains.filter((d) => d.status !== 'verified');
        return (
          <div className="text-xs" data-testid="idp-routing-cell">
            <span className="text-gray-800">
              {verified.length ? `Users ${verified.map((d) => `@${domainLabel(d)}`).join(', ')}` : 'Nobody (no verified domain)'}
            </span>
            {pending.map((d) => (
              <span key={d.id} className="block text-gray-400">@{domainLabel(d)} — pending</span>
            ))}
          </div>
        );
      },
    },
    {
      key: 'test',
      label: 'Connectivity',
      render: (p) => {
        const r = testResults[p.id];
        if (r === 'running') return <span className="text-xs text-gray-400">testing…</span>;
        if (!r) return <span className="text-xs text-gray-400">not tested</span>;
        return r.ok ? (
          <span className="inline-flex items-center gap-1 text-xs text-success-700"><CheckCircle2 className="w-3.5 h-3.5" /> OK · {r.latencyMs} ms</span>
        ) : (
          <span className="inline-flex items-center gap-1 text-xs text-danger-700" title={r.error}><XCircle className="w-3.5 h-3.5" /> failed</span>
        );
      },
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (p) => (
        <div className="flex items-center gap-2">
          <button type="button" className="p-1.5 rounded hover:bg-gray-100 text-gray-500" title="Test connection" onClick={() => void runTest(p)}>
            <PlugZap className="w-4 h-4" />
          </button>
          <button type="button" className="p-1.5 rounded hover:bg-gray-100 text-gray-500" title="Edit / rotate secret" onClick={() => setEditorTarget(p)}>
            <Pencil className="w-4 h-4" />
          </button>
          <button type="button" className="p-1.5 rounded hover:bg-gray-100 text-gray-400 hover:text-danger-600" title="Delete provider" onClick={() => void startDelete(p)}>
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Identity Providers</h1>
          <p className="text-gray-600 mt-1">
            Let this tenant's users sign in with their own Azure AD / OIDC provider. Their IdP groups map onto{' '}
            <span className="font-medium">User Groups</span> for Secure Access policy.
          </p>
        </div>
        <div className="flex space-x-2">
          <button type="button" className="btn-secondary" onClick={() => void refreshAfterWrite()} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 inline ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button type="button" className="btn-primary" onClick={() => setEditorTarget('new')}>
            <Plus className="w-4 h-4 mr-2 inline" />
            Add Provider
          </button>
        </div>
      </div>

      {error && <div className="rounded-md border border-danger-200 bg-danger-50 px-4 py-3 text-sm text-danger-700">{error}</div>}

      <div className="grid grid-cols-3 gap-4">
        <StatCard title="Providers" value={providers.length} icon={KeyRound} color="primary" loading={loading} />
        <StatCard title="Enabled" value={providers.filter((p) => p.enabled).length} icon={KeyRound} color="success" loading={loading} />
        <StatCard title="Azure AD" value={providers.filter((p) => p.providerType === 'azuread').length} icon={KeyRound} color="info" loading={loading} />
      </div>

      {providers.length > 0 && (
        <div className="card space-y-2">
          <h2 className="text-sm font-semibold text-gray-900">Register on the IdP side</h2>
          <p className="text-xs text-gray-500">
            In the customer's IdP app registration, add the redirect URI below (one per provider). For Azure AD also add the
            <span className="font-mono"> groups </span>claim under Token configuration — Entra emits group <em>object IDs</em>, which is what you link on User Groups.
          </p>
          <ul className="space-y-1">
            {providers.map((p) => (
              <li key={p.id} className="flex items-center justify-between gap-3 rounded border border-gray-200 bg-gray-50 px-3 py-2 text-xs">
                <span className="text-gray-700 font-medium shrink-0">{p.displayName}</span>
                <code className="text-gray-700 break-all flex-1">{p.brokerEndpoint}</code>
                <button type="button" className="p-1 rounded hover:bg-gray-200 text-gray-500" title="Copy redirect URI" onClick={() => void copy(p.brokerEndpoint, 'Redirect URI')}>
                  <Copy className="w-3.5 h-3.5" />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="card">
        <DataTable
          columns={columns}
          data={providers}
          loading={loading}
          searchPlaceholder="Search providers…"
          emptyMessage="No external identity provider yet — users sign in with the built-in directory"
        />
      </div>

      {/* Sign-in routing by email domain (28d §10.1): one card per provider. */}
      {providers.length > 0 && (
        <div className="space-y-4">
          <p className="text-xs text-gray-500" data-testid="idp-tenant-mode">
            {mode === 'domains'
              ? 'This tenant routes sign-in by email domain: users of a verified domain sign in through its provider, everyone else with their SecureLink password.'
              : mode === 'all'
                ? 'Every user of this tenant signs in through its identity provider. The new SecureLink apps find the provider by email domain before sign-in, so list and verify its domains; switch it to "Only these domains" to let everyone else use their SecureLink password.'
                : 'No provider is enabled: every user signs in with their SecureLink password.'}
          </p>
          {providers.map((p) => (
            <ProviderRoutingCard key={p.id} provider={p} providers={providers} isSuperAdmin={isSuperAdmin} onChanged={refreshAfterWrite} />
          ))}
        </div>
      )}

      {isSuperAdmin && <ClaimedDomainsCard refreshKey={activityKey} onChanged={refreshAfterWrite} />}

      <IdpActivityCard refreshKey={activityKey} />

      <ProviderEditor
        target={editorTarget}
        onClose={() => setEditorTarget(null)}
        onSaved={async () => {
          setEditorTarget(null);
          await refreshAfterWrite();
        }}
      />

      <RoutingPreviewDialog
        isOpen={!!deletePreview}
        title={deletePreview ? `Delete ${deletePreview.provider.displayName}` : ''}
        description={
          <span>
            Delete <strong>{deletePreview?.provider.displayName}</strong>? Its external group links and its domains are removed
            with it{mode === 'domains' ? ', and its users move to their SecureLink password' : ''}.
          </span>
        }
        preview={deletePreview?.preview ?? null}
        loading={deletePreview?.loading}
        error={deletePreview?.error}
        busy={deleting}
        variant="danger"
        providerName={deletePreview?.provider.displayName}
        confirmText="Delete"
        onConfirm={() => void confirmPreviewDelete()}
        onCancel={() => setDeletePreview(null)}
      />

      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Delete identity provider"
        message={
          <span>
            Delete <strong>{deleteTarget?.displayName}</strong>? Users who signed in through it can no longer log in this way; its
            external group links are removed with it.
          </span>
        }
        confirmText="Delete"
        variant="danger"
        isLoading={deleting}
      />
    </div>
  );
};

/** Create / edit modal. Plain state: five fields, one conditional. The secret field is write-only. */
const ProviderEditor = ({
  target,
  onClose,
  onSaved,
}: {
  target: IdentityProvider | null | 'new';
  onClose: () => void;
  onSaved: () => Promise<void>;
}) => {
  const toast = useToast();
  const isNew = target === 'new';
  const editing = target && target !== 'new' ? target : null;

  const [providerType, setProviderType] = useState<IdentityProviderType>('azuread');
  const [displayName, setDisplayName] = useState('');
  const [azureTenantId, setAzureTenantId] = useState('');
  const [issuer, setIssuer] = useState('');
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState<IdentityProvider | null>(null);
  // Disabling a provider of a domain-mode tenant moves its users (28d §7.3):
  // the API answers 409 CONFIRM_REQUIRED with the preview, shown here.
  const [confirmPreview, setConfirmPreview] = useState<RoutingPreview | null>(null);

  // Seed the form whenever the modal is (re)opened for a different target.
  useEffect(() => {
    if (!target) return;
    setCreated(null);
    setConfirmPreview(null);
    setClientSecret('');
    if (target === 'new') {
      setProviderType('azuread');
      setDisplayName('');
      setAzureTenantId('');
      setIssuer('');
      setClientId('');
      setEnabled(true);
    } else {
      setProviderType(target.providerType);
      setDisplayName(target.displayName);
      setIssuer(target.issuer);
      setClientId(target.clientId);
      setEnabled(target.enabled);
    }
  }, [target]);

  const submit = async (confirm = false) => {
    try {
      setSaving(true);
      if (isNew) {
        if (!displayName.trim() || !clientId.trim() || !clientSecret) {
          toast.error('Missing fields', 'Name, client ID and client secret are required');
          return;
        }
        const p = await accessService.createIdentityProvider({
          providerType,
          displayName: displayName.trim(),
          azureTenantId: providerType === 'azuread' ? azureTenantId.trim() : undefined,
          issuer: providerType === 'oidc' ? issuer.trim() : undefined,
          clientId: clientId.trim(),
          clientSecret,
        });
        toast.success('Identity provider created', `${p.displayName} — register the redirect URI on the IdP side`);
        setCreated(p);
      } else if (editing) {
        await accessService.updateIdentityProvider(editing.id, {
          displayName: displayName.trim() || undefined,
          clientId: clientId.trim() || undefined,
          clientSecret: clientSecret || undefined,
          enabled,
          ...(confirm ? { confirm: true } : {}),
        });
        toast.success('Identity provider updated', clientSecret ? 'Client secret rotated' : displayName);
        setConfirmPreview(null);
        await onSaved();
      }
    } catch (err) {
      const preview = !isNew ? confirmRequiredPreview(err) : null;
      if (preview) setConfirmPreview(preview);
      else toast.error(isNew ? 'Create failed' : 'Update failed', apiErrorMessage(err, 'Request failed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={!!target}
      onClose={() => (created ? void onSaved() : onClose())}
      title={isNew ? 'Add Identity Provider' : `Edit — ${editing?.displayName ?? ''}`}
      size="lg"
      footer={
        created ? (
          <div className="flex justify-end">
            <button type="button" className="btn-primary" onClick={() => void onSaved()}>Done</button>
          </div>
        ) : (
          <div className="flex justify-end space-x-2">
            <button type="button" className="btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
            <button type="button" className="btn-primary" onClick={() => void submit(false)} disabled={saving}>
              {saving ? 'Saving…' : isNew ? 'Create Provider' : 'Save'}
            </button>
          </div>
        )
      }
    >
      {created ? (
        <div className="space-y-3 text-sm">
          <p className="text-gray-700">
            <strong>{created.displayName}</strong> is configured. Finish on the IdP side by registering this redirect URI:
          </p>
          <code className="block rounded border border-gray-200 bg-gray-50 px-3 py-2 text-xs break-all">{created.brokerEndpoint}</code>
          <p className="text-xs text-gray-500">
            Then link the IdP's groups to this tenant's User Groups (Administration → User Groups → Members → External groups).
            Clients sign in with hint <span className="font-mono">{created.loginHint}</span>.
          </p>
          {created.routing === 'domains' && (
            <p className="text-xs text-warning-700" data-testid="idp-created-domain-mode">
              This tenant routes sign-in by email domain, so {created.displayName} routes nobody until you add and verify its
              domains (Sign-in routing, below the provider list).
            </p>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {isNew && (
            <AccessFormField label="Provider type" htmlFor="idp-type" required>
              <select id="idp-type" className="input-base w-full" value={providerType} onChange={(e) => setProviderType(e.target.value as IdentityProviderType)}>
                <option value="azuread">Azure AD (Entra ID)</option>
                <option value="oidc">Generic OIDC</option>
              </select>
            </AccessFormField>
          )}
          <AccessFormField label="Display name" htmlFor="idp-name" required>
            <input id="idp-name" className="input-base w-full" placeholder="Contoso Entra ID" value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
          </AccessFormField>
          {isNew && providerType === 'azuread' && (
            <AccessFormField label="Azure tenant ID" htmlFor="idp-azure-tenant" required hint="The customer's Entra tenant GUID (or verified domain). The issuer becomes https://login.microsoftonline.com/<tenant>/v2.0.">
              <input id="idp-azure-tenant" className="input-base w-full font-mono" placeholder="72f988bf-86f1-41af-91ab-2d7cd011db47" value={azureTenantId} onChange={(e) => setAzureTenantId(e.target.value)} />
            </AccessFormField>
          )}
          {isNew && providerType === 'oidc' && (
            <AccessFormField label="Issuer URL" htmlFor="idp-issuer" required hint="Discovery is read from <issuer>/.well-known/openid-configuration.">
              <input id="idp-issuer" className="input-base w-full font-mono" placeholder="https://idp.example.com/realms/corp" value={issuer} onChange={(e) => setIssuer(e.target.value)} />
            </AccessFormField>
          )}
          {!isNew && (
            <AccessFormField label="Issuer" htmlFor="idp-issuer-ro">
              <input id="idp-issuer-ro" className="input-base w-full font-mono bg-gray-50" value={issuer} readOnly />
            </AccessFormField>
          )}
          <AccessFormField label="Client ID" htmlFor="idp-client-id" required>
            <input id="idp-client-id" className="input-base w-full font-mono" value={clientId} onChange={(e) => setClientId(e.target.value)} />
          </AccessFormField>
          <AccessFormField
            label={isNew ? 'Client secret' : 'New client secret'}
            htmlFor="idp-client-secret"
            required={isNew}
            hint={isNew ? 'Stored only in the identity broker; never shown again.' : 'Leave blank to keep the current secret.'}
          >
            <input id="idp-client-secret" type="password" autoComplete="new-password" className="input-base w-full font-mono" value={clientSecret} onChange={(e) => setClientSecret(e.target.value)} />
          </AccessFormField>
          {!isNew && (
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
              Enabled — users may sign in through this provider
            </label>
          )}
        </div>
      )}
      <RoutingPreviewDialog
        isOpen={!!confirmPreview}
        title={`Disable ${editing?.displayName ?? 'provider'}`}
        description={<span>Saving disables {editing?.displayName}: the users it signs in move to another sign-in method (see below).</span>}
        preview={confirmPreview}
        providerName={editing?.displayName}
        busy={saving}
        variant="danger"
        confirmText="Save and disable"
        onConfirm={() => void submit(true)}
        onCancel={() => setConfirmPreview(null)}
      />
    </Modal>
  );
};

export default IdentityProviders;
